import { Alert, AlertIcon, Box, HStack, Text, VStack } from "@chakra-ui/react";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import PatientProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import {
  AddCaregiverEmergencyContactButton,
  CaregiverEmergencyContactCard,
  CaregiverEmergencyContactEditorCard,
  CaregiverEmergencyContactsGrid,
  NoCaregiverEmergencyContactsRow,
} from "./components/CaregiverEmergencyContactCards";
import { useCaregiverProfileEmergencyContactController } from "./CaregiverProfileEmergencyContactSection.hooks";

type Props = {
  caregiver: CaregiverProfileCaregiver;
};

export default function CaregiverProfileEmergencyContactSection(props: Props) {
  const controller = useCaregiverProfileEmergencyContactController(props.caregiver);

  return (
    <VStack alignItems="stretch" spacing={4}>
      {controller.canEdit ? (
        <HStack justifyContent="flex-end">
          {controller.isEditing ? (
            <EntityModalButton
              isLoading={controller.isSaving}
              kind="blue"
              size="sm"
              onClick={controller.saveEmergencyContacts}
            >
              Save
            </EntityModalButton>
          ) : null}
          <PatientProfileEditModeButton
            isDisabled={controller.isSaving}
            isEditing={controller.isEditing}
            label="Edit"
            onClick={controller.isEditing ? controller.cancelEditing : controller.startEditing}
          />
        </HStack>
      ) : null}

      {controller.isEditing ? (
        <VStack alignItems="stretch" spacing={4}>
          {controller.validationMessages ? (
            <Alert status="error">
              <AlertIcon />
              <Box>
                {controller.validationMessages.map((message) => (
                  <Text key={message}>{message}</Text>
                ))}
              </Box>
            </Alert>
          ) : null}
          {controller.draftEmergencyContacts.map((contact, index) => (
            <CaregiverEmergencyContactEditorCard
              key={`caregiver-emergency-contact-${index}`}
              contact={contact}
              index={index}
              isDisabled={controller.isSaving}
              showErrors={controller.showErrors}
              onChange={controller.updateEmergencyContact}
              onRemove={controller.removeEmergencyContact}
            />
          ))}
          {controller.draftEmergencyContacts.length === 0 ? (
            <NoCaregiverEmergencyContactsRow
              isDisabled={controller.isSaving}
              isEditing={true}
              onAdd={controller.addEmergencyContact}
            />
          ) : (
            <Box>
              <AddCaregiverEmergencyContactButton
                contactCount={controller.draftEmergencyContacts.length}
                isDisabled={controller.isSaving}
                onClick={controller.addEmergencyContact}
              />
            </Box>
          )}
        </VStack>
      ) : controller.emergencyContacts.length === 0 ? (
        <NoCaregiverEmergencyContactsRow
          isDisabled={controller.isSaving}
          isEditing={false}
          onAdd={controller.addEmergencyContact}
        />
      ) : (
        <CaregiverEmergencyContactsGrid>
          {controller.emergencyContacts.map((contact, index) => (
            <CaregiverEmergencyContactCard
              key={`caregiver-emergency-contact-${index}`}
              contact={contact}
              index={index}
            />
          ))}
        </CaregiverEmergencyContactsGrid>
      )}
    </VStack>
  );
}
